import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { sessionsApi } from '../api/sessions';
import { storiesApi } from '../api/stories';
import { resolveMostRecentContext } from '../utils/mostRecentContext';
import { isAbortError } from '../api/client';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import { forgetOpenProject, readLastProjectId } from '../utils/lastProject';
import { openBookChatPath } from '../utils/openBookChat';
import { withOneQuietRetry } from '../utils/loadChatSession';
import { BOOK_LOAD_FAILED, TRY_AGAIN } from '../utils/writerCopy';
import { chatPageRoomTitle } from '../utils/chatPageRoom';
import ChatInterface from '../components/Chat/ChatInterface';
import IdeaRoomBar from '../components/Chat/IdeaRoomBar';
import ThemeWorkshopLayout from '../components/Workshop/ThemeWorkshopLayout';
import MigrationNotice from '../components/Workshop/MigrationNotice';
import { WorkshopBreadcrumb } from '../components/Workshop/WorkshopBreadcrumb';

function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const {
    updateCurrentSession,
    currentProject,
    currentStory,
    currentSession,
    projectOpenError,
  } = useApp();
  const [redirecting, setRedirecting] = useState(!sessionId);
  const [redirectError, setRedirectError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const storyMissRetries = useRef({ sessionId: undefined, phase: 'idle' });

  // Parent session for breadcrumb navigation (exploration spawning)
  const [parentSession, setParentSession] = useState(null);

  // No sessionId in the URL: redirect to the most-recently-active session.
  useEffect(() => {
    if (sessionId) {
      setRedirecting(false);
      setRedirectError(null);
      return undefined;
    }
    const direct = openBookChatPath({ currentSession, currentStory, currentProject });
    if (direct) {
      navigate(direct, { replace: true });
      return undefined;
    }
    // A remembered book is still hydrating. Wait for it instead of
    // opening whichever project was active most recently.
    if (!currentProject && readLastProjectId() && !projectOpenError) {
      return undefined;
    }
    let cancelled = false;
    setRedirecting(true);
    setRedirectError(null);
    updateCurrentSession(null);
    const scope = currentProject ? { project: currentProject } : {};
    withOneQuietRetry(() => resolveMostRecentContext(scope))
      .then(({ session: mostRecentSession }) => {
        if (cancelled) return;
        if (mostRecentSession) {
          navigate(`/chat/${mostRecentSession.id}`, { replace: true });
          return;
        }
        // No sessions found - stay on /chat with no session
        setRedirecting(false);
      })
      .catch((err) => {
        if (cancelled || isAbortError(err)) return;
        console.error('Failed to load session context:', err);
        setRedirectError(BOOK_LOAD_FAILED);
        setRedirecting(false);
      });
    return () => { cancelled = true; };
  }, [
    sessionId,
    navigate,
    updateCurrentSession,
    loadAttempt,
    currentSession,
    currentStory,
    currentProject,
    projectOpenError,
  ]);

  const { session, sessionLoading, sessionError } = useSyncStoryContext({
    sessionId,
    reloadKey: loadAttempt,
  });

  // Keep context's currentSession in sync with the resolved session record.
  useEffect(() => {
    if (sessionId) updateCurrentSession(session ?? null);
  }, [sessionId, session, updateCurrentSession]);

  const loading = redirecting || (!!sessionId && sessionLoading);
  const error = redirectError || sessionError;
  // The story route says "Story {id} not found". Match that and the
  // shorter "Story not found" form.
  const storyMissing = /story\b[\s\S]*\bnot found/i.test(String(sessionError || ''));

  // One more story read before leaving the book. The hook's own retry can
  // finish without a state change when the error text is unchanged, so the
  // decision lives on this read instead of on a second render.
  useEffect(() => {
    const retry = storyMissRetries.current;
    if (retry.sessionId !== sessionId) {
      storyMissRetries.current = { sessionId, phase: 'idle' };
    }
    if (!storyMissing || sessionLoading) return undefined;
    if (storyMissRetries.current.phase !== 'idle') return undefined;
    storyMissRetries.current.phase = 'probing';
    const attempt = storyMissRetries.current;
    const storyId = session?.story_id;
    (async () => {
      try {
        if (!storyId) throw new Error('Story not found');
        await storiesApi.get(storyId);
        if (storyMissRetries.current !== attempt || attempt.phase !== 'probing') return;
        attempt.phase = 'reloading';
        setLoadAttempt((n) => n + 1);
      } catch {
        if (storyMissRetries.current !== attempt || attempt.phase !== 'probing') return;
        attempt.phase = 'left';
        forgetOpenProject();
        navigate('/projects', { replace: true });
      }
    })();
    return undefined;
  }, [storyMissing, sessionLoading, sessionId, session, navigate]);

  // Fetch parent session info if this session was spawned from a logline workshop
  useEffect(() => {
    async function fetchParentSession() {
      if (session?.parent_session_id) {
        try {
          // Use sessionsApi which handles auth
          const parentData = await sessionsApi.get(session.parent_session_id);
          // Only show breadcrumb if parent is a logline workshop
          if (parentData?.template === 'logline') {
            setParentSession(parentData);
          } else {
            setParentSession(null);
          }
        } catch (err) {
          console.error('Failed to fetch parent session:', err);
          setParentSession(null);
        }
      } else {
        setParentSession(null);
      }
    }
    fetchParentSession();
  }, [session]);

  // Handler for returning to the logline workshop
  const handleReturnToLogline = () => {
    if (parentSession?.id) {
      navigate(`/chat/${parentSession.id}`);
    }
  };

  if (loading) {
    return (
      <div className="chat-page">
        <div className="chat-page__loading">Loading...</div>
      </div>
    );
  }

  if (storyMissing) {
    return (
      <div className="chat-page">
        <div className="chat-page__loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-page">
        <div className="chat-page__error" role="alert">
          <p>{BOOK_LOAD_FAILED}</p>
          <button type="button" className="chat-page__retry" onClick={() => setLoadAttempt((n) => n + 1)}>
            {TRY_AGAIN}
          </button>
        </div>
      </div>
    );
  }

  const roomTitle = chatPageRoomTitle(session);

  // Check workshop type - different workshops need different layouts.
  // The Story Board is the sole structure surface now; all structural
  // workshop sessions (including retired workshop_skeleton ones) get a
  // migration notice pointing to the Board (their outline sections remain
  // the story's outline). Theme keeps its own layout — it was never part
  // of the structure surface.
  const isThemeWorkshop = session?.template === 'workshop_theme';
  const isLegacyStructuralWorkshop =
    (session?.template?.startsWith('workshop_') || session?.template === 'outline_section') &&
    !isThemeWorkshop;

  return (
    <div className="chat-page">
      {isThemeWorkshop ? (
        <ThemeWorkshopLayout
          sessionId={sessionId}
          storyId={session?.story_id}
          onBack={() => navigate(-1)}
        />
      ) : isLegacyStructuralWorkshop ? (
        <MigrationNotice
          session={session}
          onBack={() => navigate(-1)}
        />
      ) : (
        <>
          {/* Show breadcrumb for sessions spawned from logline workshop */}
          {parentSession && (
            <WorkshopBreadcrumb
              parentSession={parentSession}
              onReturn={handleReturnToLogline}
            />
          )}
          <IdeaRoomBar
            storyId={session?.story_id || currentStory?.id || null}
            projectId={currentProject?.id || null}
            storyName={currentStory?.name || ''}
            sessionId={sessionId}
            title={roomTitle}
          />
          <ChatInterface
            sessionId={sessionId}
            roomKey="idea"
            subject={roomTitle}
          />
        </>
      )}
    </div>
  );
}

export default ChatPage;
